import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { getIn } from "formik";
import cx from "classnames";
import { RadioGroup, FormControl, FormGroup } from "@mui/material";
import { styled } from "@mui/system";

import { CustomCheckbox } from "./CustomCheckbox";
import { CustomRadioButton } from "../RadioButton/CustomRadioButton";
import { ErrorMessage, ContexualHelp } from "../../Notifications";
import { InfoTitle } from "../../InfoTitle";

/**
 * CheckboxGroup Component
 *
 * Renders a group of checkboxes or radio buttons with optional contextual help and error display.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} [props.typeRadio] - If true, renders radio buttons instead of checkboxes
 * @param {Array} props.options - Options to render
 * @param {Function} [props.filterOptions] - Function to filter options
 * @param {Function} [props.extractId] - Function to extract unique id from option
 * @param {Function} [props.extractValue] - Function to extract value from option
 * @param {Function} [props.extractLabel] - Function to extract label from option
 * @param {string} [props.infoTitle] - Info title for the group
 * @param {Object} props.field - Formik field object
 * @param {Object} props.form - Formik form object (must contain errors, touched)
 * @param {Function} [props.onSelect] - Handler for option select
 * @param {React.ReactNode} [props.textArea] - Optional textarea node
 * @param {Object} [props.classes] - Additional classes
 * @param {string} [props.contextualHelpText] - Contextual help text
 * @param {Object} [props.contextualHelpProps] - Contextual help props
 * @param {boolean} [props.disabled] - If true, disables all checkboxes
 * @param {boolean} [props.customIcon] - If true, uses custom icon
 * @param {string} [props.typeOfCheckbox] - Type of checkbox (default: 'checkbox')
 * @param {boolean} [props.isInlineStyle] - If true, renders inline
 * @param {Function} [props.clickHandled] - Custom click handler
 * @returns {React.ReactElement}
 */

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  "& .selectedRadioButton": {
    border: "1px solid var(--Color-Text-Primary-On-Light, #1F1F1F)"
  }
}));

const DISABLED_CURRENCY_CODE = "AED";

export const CheckboxGroup = ({
  typeRadio,
  options,
  filterOptions = options => options,
  extractId = option => option.key,
  extractValue = option => option.value,
  extractLabel = item => item.label || item.displayText,
  infoTitle,
  field,
  form: { errors, touched },
  onSelect = () => { },
  textArea,
  classes: extendedClasses,
  contextualHelpText,
  contextualHelpProps = {},
  disabled = false,
  customIcon = false,
  typeOfCheckbox = "checkbox",
  isInlineStyle = true,
  clickHandled
}) => {
  const errorMessage = getIn(errors, field.name);
  const hasError = errorMessage && getIn(touched, field.name);

  const opts = useMemo(() => filterOptions(options), [options, filterOptions]);

  const disabledCheckboxByDefault = [DISABLED_CURRENCY_CODE];

  return (
    <StyledFormControl className="formControl">
      <ContexualHelp title={contextualHelpText} {...contextualHelpProps}>
        {typeRadio ? (
          <RadioGroup
            {...field}
            row={isInlineStyle}
            onChange={clickHandled ? () => { } : field?.onChange}
            onBlur={clickHandled ? () => { } : field?.onBlur}
            sx={{
              gap: "16px"
            }}
          >
            {opts.map(item => (
              <CustomRadioButton
                key={extractId(item)}
                value={extractValue(item)}
                label={extractLabel(item)}
                onSelect={onSelect}
                classes={{
                  ...extendedClasses,
                  root: cx(
                    field.value === item.value && "selectedRadioButton", //for selected radio button style
                    extendedClasses?.root
                  )
                }}
                customIcon={customIcon}
                radioColor="secondary"
                data-testid={extractId(item)}
              />
            ))}
            {textArea}
          </RadioGroup>
        ) : (
          <FormGroup row={isInlineStyle}>
            {opts.map(item => (
              <CustomCheckbox
                typeOfCheckbox={typeOfCheckbox}
                {...field}
                key={extractId(item)}
                value={extractValue(item)}
                label={extractLabel(item)}
                onSelect={onSelect}
                disabled={disabledCheckboxByDefault.includes(item.value) || disabled}
                checked={(field.value || []).includes(extractValue(item))}
                classes={extendedClasses}
                onChange={clickHandled ? () => { } : field.onChange}
                data-testid={extractId(item)}
              />
            ))}
          </FormGroup>
        )}
      </ContexualHelp>

      {hasError && <ErrorMessage error={errorMessage} />}
      {infoTitle && <InfoTitle title={infoTitle} />}
    </StyledFormControl>
  );
};

CheckboxGroup.propTypes = {
  typeRadio: PropTypes.bool,
  options: PropTypes.array.isRequired,
  filterOptions: PropTypes.func,
  extractId: PropTypes.func,
  extractValue: PropTypes.func,
  extractLabel: PropTypes.func,
  infoTitle: PropTypes.string,
  field: PropTypes.object.isRequired,
  form: PropTypes.shape({
    errors: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired
  }).isRequired,
  onSelect: PropTypes.func,
  textArea: PropTypes.node,
  classes: PropTypes.object,
  contextualHelpText: PropTypes.string,
  contextualHelpProps: PropTypes.object,
  disabled: PropTypes.bool,
  customIcon: PropTypes.bool,
  typeOfCheckbox: PropTypes.string,
  isInlineStyle: PropTypes.bool,
  clickHandled: PropTypes.func
};
